/* ===================================================================
   Settings - vertical subnav with six panels: Business profile, Brand
   voice, Notifications, Integrations, Team, Billing.

   WIRED (2026-06-17, Brad):
     - Business profile + Brand voice -> GET/PUT /api/v1/business
       (load current, Save persists; brand_voice feeds AI drafts).
     - Notifications -> GET/PUT /api/v1/notification-preferences (already live).
     - Integrations -> GET /me -> googleConnectUrl(client_id) launches the
       real GBP OAuth flow. Only connectors that actually work are surfaced.
     - Team -> GET/POST /api/v1/team (list members, invite).
     - Billing -> LIVE (2026-06-23). Subscribe/upgrade via Stripe-hosted
       Checkout: GET /billing/plan (current plan), GET /billing/consent-text
       (links), POST /billing/checkout (redirect to Stripe). Checkout is
       blocked until the REQUIRED Terms of Service / Privacy box is checked.
       SMS authorization is a SEPARATE, OPTIONAL box (sms_authorized); declining
       still completes the subscription (Fix B1 de-bundle, 2026-06-30). See
       BillingPanel below.

   PRICING (ONE PRICE, locked 2026-08-06 -- retires the Starter/Pro ladder):
     $59/4 weeks, every feature included, same for everyone. Plain
     month-to-month: after the 14-day free trial the first charge is ONE 4-week
     cycle ($59), then every 4 weeks. NO upfront multi-cycle charge.
   Multi-location is +$25/4 weeks per extra location, available to EVERY
   customer. 14-day free trial, CARD REQUIRED (disclosed only at Stripe
   checkout). UI copy must NEVER say "no card" or "card required" - just
   "cancel anytime". Do not reintroduce any retired figure: the $49/$99 tiers,
   the $147/$297 upfront model, the $950/yr annual plan, the $75/$125 standard
   tier, or the $39 Missed-Call Text-Back add-on.

   HARD PRODUCT RULE - Brand voice configures AI *drafts* only. A drafted
   reply is never posted without explicit human approval on Reviews.
   =================================================================== */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardHead, Icon, Avatar, Chip, Switch, SearchPath } from '../components/ui';
import { ClientPhotoUpload } from '../components/ClientPhotoUpload';
import VendorLogo from '../components/VendorLogo';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useCadencePreferences,
  useUpdateCadencePreferences,
  useBusiness,
  useUpdateBusiness,
  usePopulateFromPlace,
  usePlacesSearch,
  useMe,
  planLabel,
  usePlan,
  useFeedbackGate,
  useUpdateFeedbackGate,
  useTeam,
  useInviteTeamMember,
  useConnectors,
  type NotificationPreferencesPatch,
} from '../hooks';
import {
  api,
  startGoogleConnect,
  setToken,
  type BrandVoice,
  type TeamRole,
  type MctbResponse,
  type MctbUpdate,
  type BillingPlan,
  type ConsentText,
  type BillingTier,
  type PlaceMatch,
  type FeedbackRoute,
} from '../lib/api';
import { clearOnboardingState } from './Onboarding';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiToken } from '../hooks/useApiToken';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../auth/useSession';
import '../styles/components.css';
import '../styles/settings.css';

const PlusPath = <path d="M12 5v14M5 12h14" strokeLinecap="round" />;

/* ---------------------------------------------------------------------
   PARKED FEATURE FLAG: Missed-Call Text-Back (MCTB) UI.
   Set false 2026-06-24 so the MCTB nav tab + panel are HIDDEN from every
   logged-in user (incl. the public demo account) while the Twilio SMS
   A2P review is open. The MctbPanel component + missed_call API + backend
   are intentionally left in place (parked, not deleted). Re-expose at
   MCTB relaunch by flipping this to true and rebuilding/redeploying.

   2026-08-06 (Steve): the $39-every-4-weeks add-on was pulled from the OFFER
   as well, not just the UI. The marketing site no longer sells it and the
   checkout route ignores an "mctb" flag (_MCTB_PURCHASABLE in
   engine/api/v1/billing.py). The feature itself is untouched. Before flipping
   this flag back on, confirm the price with Steve -- the "$39/4 weeks"
   strings in MctbPanel below are the OLD offer and must not go back in front
   of a customer unpriced or unapproved.
   --------------------------------------------------------------------- */
const MCTB_UI_ENABLED = false;

/* The prototype's Settings is a single scrolling column, not a subnav. The old
   PanelId union + NAV list that drove the retired subnav were removed with that
   layout (2026-07-03, Corbato); each panel is now stacked directly in the
   column by the Settings() export below. */

/* ---------------------------------------------------------------------
   Notifications panel (live, wired to the prefs hooks) - unchanged.
   --------------------------------------------------------------------- */
type DraftPrefs = {
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  trigger_scope: 'all_reviews' | 'negative_only' | 'milestones_too';
  positives_mode: 'instant' | 'digest' | 'off';
  digest_frequency: string;
  owner_email: string;
  owner_phone: string;
};

function NotificationsPanel() {
  const prefsQuery = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();

  const [draft, setDraft] = useState<DraftPrefs | null>(null);
  const [savedAt, setSavedAt] = useState(false);

  const loaded = prefsQuery.data?.preferences;
  useEffect(() => {
    if (loaded && draft === null) {
      setDraft({
        email_enabled: loaded.email_enabled,
        sms_enabled: loaded.sms_enabled,
        push_enabled: loaded.push_enabled,
        trigger_scope: loaded.trigger_scope,
        positives_mode: loaded.positives_mode,
        digest_frequency: loaded.digest_frequency,
        owner_email: loaded.owner_email ?? '',
        owner_phone: loaded.owner_phone ?? '',
      });
    }
  }, [loaded, draft]);

  function set<K extends keyof DraftPrefs>(key: K, value: DraftPrefs[K]) {
    setSavedAt(false);
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  function handleSave() {
    if (!draft) return;
    const patch: NotificationPreferencesPatch = {
      email_enabled: draft.email_enabled,
      sms_enabled: draft.sms_enabled,
      push_enabled: draft.push_enabled,
      trigger_scope: draft.trigger_scope,
      positives_mode: draft.positives_mode,
      digest_frequency: draft.digest_frequency,
      owner_email: draft.owner_email.trim() || null,
      owner_phone: draft.owner_phone.trim() || null,
    };
    update.save(patch).then(
      () => setSavedAt(true),
      () => {
        /* error surfaced via update.error below */
      },
    );
  }

  if (prefsQuery.isLoading || (prefsQuery.fetchStatus === 'idle' && !loaded)) {
    return (
      <Card>
        <CardHead title="Notifications" />
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {prefsQuery.isError
            ? 'We could not load your notification settings. Please try again shortly.'
            : 'Loading your notification settings...'}
        </p>
      </Card>
    );
  }

  if (!draft) {
    return (
      <Card>
        <CardHead title="Notifications" />
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          Loading your notification settings...
        </p>
      </Card>
    );
  }

  const effEmail = loaded?.effective_owner_email ?? '';
  const effPhone = loaded?.effective_owner_phone ?? '';

  return (
    <Card>
      <CardHead
        title="Notifications"
        right={<span className="link muted">New-review alerts</span>}
      />
      <p className="muted" style={{ fontSize: 12.5, marginTop: -4, marginBottom: 18, lineHeight: 1.55 }}>
        Email is the default way we reach you. SMS is opt-in and used for
        negative reviews, sent to your own number. The in-app bell is always
        on. We only ever alert you. Replies are never posted on their own.
      </p>

      <div className="set-toggles" style={{ display: 'grid', gap: 14, marginBottom: 18 }}>
        <Switch label="Email alerts (recommended)" checked={draft.email_enabled} onChange={(v) => set('email_enabled', v)} />
        <Switch label="Text me about negative reviews (SMS, opt-in)" checked={draft.sms_enabled} onChange={(v) => set('sms_enabled', v)} />
        <Switch label="Push notifications" checked={draft.push_enabled} onChange={(v) => set('push_enabled', v)} />
      </div>

      <div className="formgrid">
        <label className="fld">
          <span className="lab">Alert me about</span>
          <select className="sel" value={draft.trigger_scope} onChange={(e) => set('trigger_scope', e.target.value as DraftPrefs['trigger_scope'])}>
            <option value="all_reviews">All reviews</option>
            <option value="negative_only">Negative reviews only</option>
            <option value="milestones_too">Negatives plus milestones</option>
          </select>
        </label>
        <label className="fld">
          <span className="lab">Positive reviews</span>
          <select className="sel" value={draft.positives_mode} onChange={(e) => set('positives_mode', e.target.value as DraftPrefs['positives_mode'])}>
            <option value="instant">Tell me right away</option>
            <option value="digest">Roll up into a digest</option>
            <option value="off">Do not notify</option>
          </select>
        </label>
        <label className="fld">
          <span className="lab">Digest frequency</span>
          <select className="sel" value={draft.digest_frequency} onChange={(e) => set('digest_frequency', e.target.value)}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </label>
        <label className="fld">
          <span className="lab">Alert email</span>
          <input className="inp" type="email" value={draft.owner_email} placeholder={effEmail || 'Your billing email'} onChange={(e) => set('owner_email', e.target.value)} />
        </label>
        <label className="fld">
          <span className="lab">Alert phone (for SMS)</span>
          <input className="inp" type="tel" value={draft.owner_phone} placeholder={effPhone || 'Your mobile number'} onChange={(e) => set('owner_phone', e.target.value)} />
        </label>
      </div>

      <div className="actions" style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn--primary" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving...' : 'Save changes'}
        </button>
        {savedAt && !update.isPending && <span style={{ fontSize: 13, color: 'var(--green)' }}>Saved</span>}
        {update.error && !update.isPending && (
          <span style={{ fontSize: 13, color: 'var(--red)' }}>Could not save. Please try again.</span>
        )}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------------
   Business profile + Brand voice (live, GET/PUT /business).

   PULL FROM GOOGLE (2026-06-29, Brad): the panel opens with a friction-free
   business-name search (the same /places/search picker used on NAP and
   Competitors). Picking your business calls POST /business/populate-from-place
   and auto-fills the WHOLE profile -- name, address, phone, website, category,
   hours -- so the owner barely types. The place_id rides behind the scenes and
   is NEVER shown. If there is no Google match, every field is editable by hand;
   the point is the fields now EXIST (especially ADDRESS, the one that was
   missing -- we tell clients NAP matters, so we have to capture it ourselves).
   --------------------------------------------------------------------- */

/* Seven weekday rows for a simple, owner-friendly hours editor. business_hours
   persists as a string LIST; we keep a fixed Mon-Sun shape in the form and send
   the non-empty lines as the list (PUT needs a list or it 400s). */
const HOURS_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

/* Debounce a fast-changing value (the search box). Places bills per call, so we
   only fire after the owner stops typing. Same ~350ms pattern as NAP. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/* Owner-friendly plain-English summary of what Google filled. Turns the raw
   populated_fields list (e.g. ["business_name","business_address","contact_phone"])
   into "name, address, and phone". Never leaks a raw field key or the place_id. */
const POPULATED_LABELS: Record<string, string> = {
  business_name: 'name',
  business_address: 'address',
  contact_phone: 'phone',
  business_website: 'website',
  primary_category: 'category',
  business_hours: 'hours',
  industry: 'industry',
  contact_email: 'email',
};
function summarizePopulated(fields: string[]): string {
  const labels = fields
    .map((f) => POPULATED_LABELS[f] ?? f.replace(/_/g, ' '))
    .filter((v, i, a) => a.indexOf(v) === i);
  if (labels.length === 0) return 'your details';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

/* The pull-from-Google search picker. Reuses the exact NAP/Competitors picker
   pattern (debounced name + optional city -> /places/search -> pick a row).
   On pick -> POST /business/populate-from-place -> the parent refreshes the
   form and shows a confirmation. */
function ProfilePullFromGoogle({
  defaultName,
  onPopulated,
}: {
  defaultName: string;
  onPopulated: (filled: string[]) => void;
}) {
  const populate = usePopulateFromPlace();
  const [name, setName] = useState(defaultName);
  const [city, setCity] = useState('');
  // M7: do NOT auto-search on load. The field is seeded with the existing
  // business name, but the Places search only fires AFTER the owner actually
  // focuses/edits the field -- otherwise the dropdown pops open on every load
  // with out-of-area matches for the seeded name. `touched` gates the query.
  const [touched, setTouched] = useState(false);
  const debouncedName = useDebounced(name, 350);
  const debouncedCity = useDebounced(city, 350);
  const search = usePlacesSearch(touched ? debouncedName : '', debouncedCity);
  const [picked, setPicked] = useState<PlaceMatch | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const typed = touched && name.trim().length > 0;
  const settling = typed && debouncedName.trim() !== name.trim();
  const results = search.data?.results ?? [];
  const showNoMatches =
    typed && !settling && !search.isFetching && !search.isError && results.length === 0;
  const saving = populate.isPending;

  async function handlePick(match: PlaceMatch) {
    setErr(null);
    setPicked(match);
    try {
      const res = await populate.populate(match.place_id);
      onPopulated(res.populated_fields ?? []);
    } catch {
      setErr("We couldn't pull that business from Google just now. Try again, or fill the fields in below.");
      setPicked(null);
    }
  }

  return (
    <div className="intg-find" style={{ marginBottom: 18 }}>
      <b style={{ fontSize: 13 }}>Pull from Google (recommended)</b>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 10px', lineHeight: 1.5 }}>
        Find your business and we fill in the rest. Search the name as it appears
        on Google, then pick it from the list. No sign-in needed.
      </p>
      <div className="formgrid" style={{ maxWidth: 520 }}>
        <label className="fld full">
          <span className="lab">Business name</span>
          <div className="srch-wrap">
            <span className="srch-ico"><Icon d={SearchPath} /></span>
            <input
              className="inp srch-inp"
              value={name}
              placeholder="Start typing your business name"
              onFocus={() => setTouched(true)}
              onChange={(e) => {
                setTouched(true);
                setName(e.target.value);
              }}
              disabled={saving}
            />
          </div>
        </label>
        <label className="fld full">
          <span className="lab">City (optional, improves the match)</span>
          <input
            className="inp"
            value={city}
            placeholder="e.g. North Kingstown, RI"
            onChange={(e) => setCity(e.target.value)}
            disabled={saving}
          />
        </label>
      </div>

      <div style={{ maxWidth: 520 }}>
        {typed && (
          <div className="srch-results" role="listbox" aria-label="Matching businesses">
            {(search.isFetching || settling) && results.length === 0 && (
              <p className="muted srch-note">Searching...</p>
            )}
            {search.isError && (
              <p className="srch-note" style={{ color: 'var(--red)' }}>
                Could not search just now. Please try again in a moment.
              </p>
            )}
            {results.map((m) => (
              <button
                key={m.place_id}
                className="srch-row"
                role="option"
                aria-selected="false"
                disabled={saving}
                onClick={() => void handlePick(m)}
              >
                <span className="srch-row__name">{m.name}</span>
                {m.address && <span className="srch-row__addr">{m.address}</span>}
              </button>
            ))}
            {showNoMatches && (
              <p className="muted srch-note">
                No matches. Try adding a city, or fill in the fields below by hand.
              </p>
            )}
          </div>
        )}
        {saving && picked && (
          <p className="muted srch-note">Pulling {picked.name} from Google...</p>
        )}
        {err && (
          <p style={{ color: 'var(--red)', fontSize: 12.5, marginTop: 8 }}>{err}</p>
        )}
      </div>
    </div>
  );
}

type ProfileDraft = {
  business_name: string;
  industry: string;
  contact_phone: string;
  contact_email: string;
  business_address: string;
  business_website: string;
  primary_category: string;
  /* Fixed Mon-Sun shape in the form; persisted as a list of non-empty lines. */
  hours: string[];
};

/* Build the 7-slot form hours array from whatever list the backend returned.
   We try to line up "Monday: ..." style lines to their weekday; anything that
   doesn't match a day name falls into the next open slot, so we never drop a
   line the backend stored. */
function hoursToForm(list: string[] | undefined): string[] {
  const slots = ['', '', '', '', '', '', ''];
  if (!list || list.length === 0) return slots;
  const leftover: string[] = [];
  for (const raw of list) {
    const line = (raw ?? '').trim();
    if (!line) continue;
    const di = HOURS_DAYS.findIndex((d) => line.toLowerCase().startsWith(d.toLowerCase()));
    if (di >= 0 && !slots[di]) {
      // Strip a leading "Monday:" / "Monday -" prefix for the editable value.
      slots[di] = line.replace(new RegExp('^' + HOURS_DAYS[di] + '\\s*[:\\-]?\\s*', 'i'), '').trim();
    } else {
      leftover.push(line);
    }
  }
  // Drop any unmatched leftovers into still-empty slots in order.
  for (const extra of leftover) {
    const open = slots.findIndex((s) => !s);
    if (open >= 0) slots[open] = extra;
  }
  return slots;
}

/* Turn the 7-slot form back into the persisted list: "Monday: 8a-5p" for each
   non-empty line. Empty days are omitted. Always a LIST (PUT requires it). */
function formToHours(slots: string[]): string[] {
  return slots
    .map((v, i) => {
      const t = (v ?? '').trim();
      return t ? `${HOURS_DAYS[i]}: ${t}` : '';
    })
    .filter(Boolean);
}

function ProfilePanel() {
  const q = useBusiness();
  const update = useUpdateBusiness();
  const me = useMe();
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [saved, setSaved] = useState(false);
  const [pulled, setPulled] = useState<string | null>(null);

  const loaded = q.data?.profile;
  // Hydrate the form from the backend. We re-hydrate after a Google pull too:
  // populate invalidates the business query, so `loaded` changes and we want the
  // freshly-pulled values in the form. Guard with a ref-ish compare via JSON so
  // we don't clobber unsaved edits on unrelated refetches.
  useEffect(() => {
    if (loaded && draft === null) {
      setDraft({
        business_name: loaded.business_name ?? '',
        industry: loaded.industry ?? '',
        contact_phone: loaded.contact_phone ?? '',
        contact_email: loaded.contact_email ?? '',
        business_address: loaded.business_address ?? '',
        business_website: loaded.business_website ?? '',
        primary_category: loaded.primary_category ?? '',
        hours: hoursToForm(loaded.business_hours),
      });
    }
  }, [loaded, draft]);

  function set<K extends keyof ProfileDraft>(k: K, v: ProfileDraft[K]) {
    setSaved(false);
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  }

  function setHour(i: number, v: string) {
    setSaved(false);
    setDraft((d) => {
      if (!d) return d;
      const hours = [...d.hours];
      hours[i] = v;
      return { ...d, hours };
    });
  }

  // After a successful Google pull, force the form to re-hydrate from the
  // freshly-saved profile and show a friendly confirmation of what filled.
  function handlePopulated(fields: string[]) {
    setDraft(null); // triggers the hydrate effect against the refetched profile
    setSaved(false);
    setPulled(summarizePopulated(fields));
  }

  function handleSave() {
    if (!draft) return;
    update
      .save({
        business_name: draft.business_name.trim(),
        industry: draft.industry.trim(),
        contact_phone: draft.contact_phone.trim(),
        contact_email: draft.contact_email.trim(),
        business_address: draft.business_address.trim(),
        business_website: draft.business_website.trim(),
        primary_category: draft.primary_category.trim(),
        business_hours: formToHours(draft.hours),
      })
      .then(() => setSaved(true), () => {});
  }

  if (!draft) {
    return (
      <Card>
        <CardHead title="Business profile" />
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {q.isError ? 'We could not load your business profile. Please try again shortly.' : 'Loading your business profile...'}
        </p>
      </Card>
    );
  }

  const defaultName = (draft.business_name || me.data?.business_name || '').trim();

  return (
    <Card>
      <CardHead title="Business profile" right={<span className="link muted">Name, address, phone</span>} />
      <p className="muted" style={{ fontSize: 12.5, marginTop: -4, marginBottom: 16, lineHeight: 1.55 }}>
        Your business details. We use these to keep your name, address, and phone
        consistent everywhere customers find you. Pull them from Google in one
        step, or fill them in below.
      </p>

      {/* Review-request photo -- the global in-app home for it (Steve, 2026-07-29).
          Onboarding often gets skipped, so this always-present control is the
          primary path for setting the photo that rides on every review request. */}
      <div style={{ marginBottom: 18 }}>
        <ClientPhotoUpload />
      </div>

      {/* Pull-from-Google search picker */}
      <ProfilePullFromGoogle defaultName={defaultName} onPopulated={handlePopulated} />

      {pulled && (
        <div className="intg-banner intg-banner--ok" role="status" style={{ marginBottom: 16 }}>
          Pulled your {pulled} from Google. Check it over below and add anything
          that is missing, like your hours.
        </div>
      )}

      <div className="formgrid">
        <label className="fld">
          <span className="lab">Business name</span>
          <input className="inp" value={draft.business_name} onChange={(e) => set('business_name', e.target.value)} />
        </label>
        <label className="fld">
          <span className="lab">Industry</span>
          <input className="inp" value={draft.industry} placeholder="e.g. Plumbing" onChange={(e) => set('industry', e.target.value)} />
        </label>
        {/* ADDRESS -- the field that was missing. Given prominence (full width). */}
        <label className="fld full">
          <span className="lab">Business address</span>
          <input
            className="inp"
            value={draft.business_address}
            placeholder="Your business street address"
            onChange={(e) => set('business_address', e.target.value)}
          />
          <span className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            The address customers see on Google, Yelp, and your other listings.
            Keeping it consistent is what your NAP score checks.
          </span>
        </label>
        <label className="fld">
          <span className="lab">Phone</span>
          <input className="inp" type="tel" value={draft.contact_phone} placeholder="Your business phone number" onChange={(e) => set('contact_phone', e.target.value)} />
        </label>
        <label className="fld">
          <span className="lab">Contact email</span>
          <input className="inp" type="email" value={draft.contact_email} onChange={(e) => set('contact_email', e.target.value)} />
        </label>
        <label className="fld">
          <span className="lab">Website</span>
          <input
            className="inp"
            type="url"
            inputMode="url"
            value={draft.business_website}
            placeholder="Your website URL"
            onChange={(e) => set('business_website', e.target.value)}
          />
        </label>
        <label className="fld">
          <span className="lab">Primary category</span>
          <input
            className="inp"
            value={draft.primary_category}
            placeholder="e.g. Plumber"
            onChange={(e) => set('primary_category', e.target.value)}
          />
        </label>
      </div>

      {/* Business hours -- a simple editable weekday list. Leave a day blank if
          you're closed; we only save the days you fill in. */}
      <div style={{ marginTop: 20 }}>
        <span className="lab" style={{ display: 'block', marginBottom: 8 }}>Business hours</span>
        <p className="muted" style={{ fontSize: 11.5, marginTop: 0, marginBottom: 10, lineHeight: 1.5 }}>
          Optional. Type your hours per day (for example, 8a-5p). Leave a day
          blank if you're closed.
        </p>
        <div className="set-hours">
          {HOURS_DAYS.map((day, i) => (
            <label className="set-hours__row" key={day}>
              <span className="set-hours__day">{day}</span>
              <input
                className="inp"
                value={draft.hours[i] ?? ''}
                placeholder="8a-5p"
                onChange={(e) => setHour(i, e.target.value)}
              />
            </label>
          ))}
        </div>
      </div>

      <div className="actions" style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn--primary" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving...' : 'Save changes'}
        </button>
        {saved && !update.isPending && <span style={{ fontSize: 13, color: 'var(--green)' }}>Saved</span>}
        {update.error && !update.isPending && (
          <span style={{ fontSize: 13, color: 'var(--red)' }}>Could not save. Please try again.</span>
        )}
      </div>
    </Card>
  );
}

type VoiceDraft = {
  tone: string;
  signature: string;
  sample_phrases: string;
  auto_draft_enabled: boolean;
};

function VoicePanel() {
  const q = useBusiness();
  const update = useUpdateBusiness();
  const [draft, setDraft] = useState<VoiceDraft | null>(null);
  const [saved, setSaved] = useState(false);

  const loaded = q.data?.brand_voice;
  useEffect(() => {
    if (q.data && draft === null) {
      const v: BrandVoice = loaded ?? {};
      setDraft({
        tone: typeof v.tone === 'string' ? v.tone : 'Warm & personal',
        signature: typeof v.signature === 'string' ? v.signature : '',
        sample_phrases: typeof v.sample_phrases === 'string' ? v.sample_phrases : '',
        auto_draft_enabled: v.auto_draft_enabled !== false,
      });
    }
  }, [q.data, loaded, draft]);

  function set<K extends keyof VoiceDraft>(k: K, v: VoiceDraft[K]) {
    setSaved(false);
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  }

  function handleSave() {
    if (!draft) return;
    update
      .save({
        brand_voice: {
          tone: draft.tone.trim(),
          signature: draft.signature.trim(),
          sample_phrases: draft.sample_phrases.trim(),
          auto_draft_enabled: draft.auto_draft_enabled,
        },
      })
      .then(() => setSaved(true), () => {});
  }

  if (!draft) {
    return (
      <Card>
        <CardHead title="Brand voice" />
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {q.isError ? 'We could not load your brand voice. Please try again shortly.' : 'Loading your brand voice...'}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHead title="Brand voice" right={<span className="link muted">Used for AI replies</span>} />
      <div className="formgrid">
        <label className="fld">
          <span className="lab">Tone</span>
          <select className="sel" value={draft.tone} onChange={(e) => set('tone', e.target.value)}>
            <option>Warm &amp; personal</option>
            <option>Professional</option>
            <option>Playful</option>
          </select>
        </label>
        <label className="fld">
          <span className="lab">Sign-off</span>
          <input className="inp" value={draft.signature} placeholder="e.g. - Mike, Owner" onChange={(e) => set('signature', e.target.value)} />
        </label>
        <label className="fld full">
          <span className="lab">Always mention</span>
          <input className="inp" value={draft.sample_phrases} placeholder="e.g. family-owned, same-day service" onChange={(e) => set('sample_phrases', e.target.value)} />
        </label>
      </div>
      <div style={{ marginTop: 16 }}>
        <Switch label="Auto-draft a reply for every new review" checked={draft.auto_draft_enabled} onChange={(v) => set('auto_draft_enabled', v)} />
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>
        Auto-draft prepares a reply in your voice for your approval. It never posts a
        reply on its own.
      </p>
      <div className="actions" style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn--primary" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving...' : 'Save changes'}
        </button>
        {saved && !update.isPending && <span style={{ fontSize: 13, color: 'var(--green)' }}>Saved</span>}
        {update.error && !update.isPending && (
          <span style={{ fontSize: 13, color: 'var(--red)' }}>Could not save. Please try again.</span>
        )}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------------
   Booking (connect-first, 2026-06-26, Katherine). Three paths, in the
   marketing-copy order:
     1. Bring your own booking link (Calendly/Square/Acuity/Jobber/etc.) ->
        surfaced as a "Book" button on the public review widget.
     2. A minimal native "request a time" fallback for owners with no tool.
     3. Google Calendar connect (read-only) -> a finished appointment triggers
        the review request. The connect itself reuses the existing Google OAuth
        button; full calendar polling is scaffolded server-side (see report).
   All three persist into the brand_voice JSON blob via PUT /business.
   --------------------------------------------------------------------- */
type BookingDraft = {
  booking_url: string;
  booking_label: string;
  booking_show_on_widget: boolean;
  request_time_enabled: boolean;
  business_hours: string;
};

function BookingPanel() {
  const q = useBusiness();
  const update = useUpdateBusiness();
  const me = useMe();
  const clientId = me.data?.client_id ?? null;
  // Canonical connected-state: the calendar-trigger "Connected" chip renders
  // ONLY at google_connection_status === 'connected', never on a token signal.
  const googleConnected = me.data?.google_connection_status === 'connected';
  const [draft, setDraft] = useState<BookingDraft | null>(null);
  const [saved, setSaved] = useState(false);

  const loaded = q.data?.brand_voice;
  useEffect(() => {
    if (q.data && draft === null) {
      const v: BrandVoice = loaded ?? {};
      setDraft({
        booking_url: typeof v.booking_url === 'string' ? v.booking_url : '',
        booking_label: typeof v.booking_label === 'string' ? v.booking_label : '',
        booking_show_on_widget: v.booking_show_on_widget !== false,
        request_time_enabled: v.request_time_enabled === true,
        business_hours: typeof v.business_hours === 'string' ? v.business_hours : '',
      });
    }
  }, [q.data, loaded, draft]);

  function set<K extends keyof BookingDraft>(k: K, v: BookingDraft[K]) {
    setSaved(false);
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  }

  function handleSave() {
    if (!draft) return;
    update
      .save({
        brand_voice: {
          booking_url: draft.booking_url.trim(),
          booking_label: draft.booking_label.trim(),
          booking_show_on_widget: draft.booking_show_on_widget,
          request_time_enabled: draft.request_time_enabled,
          business_hours: draft.business_hours.trim(),
        },
      })
      .then(() => setSaved(true), () => {});
  }

  function connectGoogle() {
    if (!clientId) return;
    void startGoogleConnect(clientId);
  }

  if (!draft) {
    return (
      <Card>
        <CardHead title="Booking" />
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {q.isError ? 'We could not load your booking settings. Please try again shortly.' : 'Loading your booking settings...'}
        </p>
      </Card>
    );
  }

  const hasLink = draft.booking_url.trim().length > 0;

  return (
    <Card>
      <CardHead title="Booking" right={<span className="link muted">Connect-first</span>} />
      <p className="muted" style={{ fontSize: 12.5, marginTop: -4, marginBottom: 18, lineHeight: 1.55 }}>
        You already have a way customers book you. Rhody Reviews works with what
        you use. Add your booking link, turn on a simple request-a-time form, or
        connect your Google Calendar so a finished job times the review request
        for you.
      </p>

      {/* 1. Bring your own booking link */}
      <div className="formgrid">
        <label className="fld full">
          <span className="lab">Your booking link</span>
          <input
            className="inp"
            type="url"
            inputMode="url"
            value={draft.booking_url}
            placeholder="Your booking link URL"
            onChange={(e) => set('booking_url', e.target.value)}
          />
          <span className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            Calendly, Square, Acuity, Jobber, or any link. We show a Book button
            next to your reviews. No new tool to learn.
          </span>
        </label>
        <label className="fld">
          <span className="lab">Button label (optional)</span>
          <input
            className="inp"
            value={draft.booking_label}
            placeholder="Book"
            onChange={(e) => set('booking_label', e.target.value)}
          />
        </label>
      </div>
      <div style={{ marginTop: 14 }}>
        <Switch
          label="Show the Book button on my review widget"
          checked={draft.booking_show_on_widget}
          onChange={(v) => set('booking_show_on_widget', v)}
        />
      </div>

      {/* 2. Native request-a-time fallback */}
      <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--line, #e3e3e7)' }}>
        <Switch
          label="No booking tool? Let customers request a time"
          checked={draft.request_time_enabled}
          onChange={(v) => set('request_time_enabled', v)}
        />
        <p className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
          Adds a simple request-a-time form to your review widget. You get a
          notification and confirm the time yourself. Nothing is auto-booked.
          {hasLink && ' Your booking link above takes priority when both are set.'}
        </p>
        {draft.request_time_enabled && (
          <label className="fld" style={{ marginTop: 12, maxWidth: 320 }}>
            <span className="lab">Business hours (optional, shown on the form)</span>
            <input
              className="inp"
              value={draft.business_hours}
              placeholder="Mon-Fri 8a-5p"
              onChange={(e) => set('business_hours', e.target.value)}
            />
          </label>
        )}
      </div>

      {/* 3. Google Calendar connect */}
      <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--line, #e3e3e7)' }}>
        <div className="intg" style={{ padding: 0 }}>
          <VendorLogo id="google" size={36} />
          <div className="t">
            <b>Google Calendar</b>
            <span>
              {googleConnected
                ? 'Connected. A finished appointment can trigger the review request automatically.'
                : 'Connect so a completed appointment triggers the review request at the right time.'}
            </span>
          </div>
          {googleConnected ? (
            <Chip kind="green">Connected</Chip>
          ) : (
            <button className="btn btn--ghost" onClick={connectGoogle} disabled={!clientId}>
              {me.isLoading ? 'Loading...' : 'Connect Google'}
            </button>
          )}
        </div>
        <p className="muted" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.5 }}>
          Calendar-triggered requests roll out as your account is approved. If
          it is not connected, your booking link and request-a-time form still
          work on their own.
        </p>
      </div>

      <div className="actions" style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn--primary" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving...' : 'Save changes'}
        </button>
        {saved && !update.isPending && <span style={{ fontSize: 13, color: 'var(--green)' }}>Saved</span>}
        {update.error && !update.isPending && (
          <span style={{ fontSize: 13, color: 'var(--red)' }}>Could not save. Please check your booking link and try again.</span>
        )}
      </div>
    </Card>
  );
}


/* KeyConnectorCard, ZapierWebhookRow and the KEY_CONNECTOR_UI copy map that
   lived here are REMOVED (Brad, 2026-08-06). They were duplicates of the
   versions in routes/Integrations.tsx, kept alive only by this panel's own
   connector list. /integrations is now the single canonical integrations
   surface, so the copies are gone rather than left to drift. */

/* =====================================================================
   Settings -> Integrations: a SUMMARY that points at the one canonical
   surface. It does NOT list connectors any more.

   WHY (Steve, 2026-08-06): "why do we have the integrations of Workiz,
   Sweep and Go, and Responsibid on the integrations page, but we don't have
   it on the settings page?"

   ROOT CAUSE. This panel used to hand-roll a SECOND connector list with its
   own markup, its own ordering and its own filter:

       c.connected || (c.configured && !!c.connect_url) || c.connect_form

   A webhook connector (Workiz / Sweep&Go / ResponsiBid) has no connect_url
   -- there is no OAuth redirect to send anyone to -- and no connect_form,
   which is the key-connector flag. So all three failed that filter and could
   never appear here, no matter how they were configured. The list was also
   missing Zapier and the "Disconnect / change listing" escape hatch, and the
   post-OAuth ?google= banner it carried was effectively dead because the
   backend returns owners to /integrations, not here.

   THE FIX IS THE STRUCTURE, NOT THREE MORE CARDS. Two screens rendering the
   same list two different ways is the actual defect -- Steve had to spot the
   divergence himself, and the next new connector TYPE would have recreated
   it. /integrations already handles every type (oauth, api_key, webhook,
   zapier) and is a strict superset of what this panel could show, so it is
   now the single place integrations live. This panel keeps what a settings
   index should have: an honest count and one button to get there.
   ===================================================================== */
export function IntegrationsPanel() {
  const navigate = useNavigate();
  const me = useMe();
  const connectors = useConnectors();

  // Count Google the same way /integrations does: ONLY the canonical
  // 'connected' status counts, never a half-finished token.
  const googleConnected = me.data?.google_connection_status === 'connected';
  const conns = connectors.data?.connectors ?? [];
  const connectedCount = conns.filter((c) => c.connected).length + (googleConnected ? 1 : 0);
  const loading = me.isLoading || connectors.isLoading;

  return (
    <Card>
      <CardHead title="Integrations" />
      <p className="muted" style={{ fontSize: 13, margin: '0 0 14px', lineHeight: 1.55 }}>
        {loading
          ? 'Checking which tools are connected...'
          : connectedCount > 0
            ? `${connectedCount} tool${connectedCount === 1 ? '' : 's'} connected. Connect your booking, invoicing and job software so finished jobs ask for a review on their own.`
            : 'Connect your booking, invoicing and job software so finished jobs ask for a review on their own.'}
      </p>
      <button className="btn btn--primary" onClick={() => navigate('/integrations')}>
        Manage integrations
      </button>
    </Card>
  );
}


/* ---------------------------------------------------------------------
   Request scheduling (live, GET/PUT /cadence-preferences).

   Client-configurable cadence controls:
     - Initial-request timing (minutes after a completed job, Track A delay).
     - Number of follow-up touches (1-3; HARD CAP 3 -- Penn's compliance cap).
     - PAUSE toggle -- stops ALL review-request sending until turned off.

   Integer knobs are clamped server-side to the compliance caps and the panel
   shows the post-clamp `effective` truth after saving. Pause is a kill-switch.
   --------------------------------------------------------------------- */
type SchedDraft = {
  track_a_delay_minutes: number;
  track_b_max_touches: number;
  paused: boolean;
};

function SchedulingPanel() {
  const q = useCadencePreferences();
  const update = useUpdateCadencePreferences();
  const [draft, setDraft] = useState<SchedDraft | null>(null);
  const [saved, setSaved] = useState(false);

  const eff = q.data?.effective;
  const limits = q.data?.limits;
  useEffect(() => {
    if (eff && draft === null) {
      setDraft({
        track_a_delay_minutes: eff.track_a_delay_minutes,
        track_b_max_touches: eff.track_b_max_touches,
        paused: eff.paused,
      });
    }
  }, [eff, draft]);

  function set<K extends keyof SchedDraft>(k: K, v: SchedDraft[K]) {
    setSaved(false);
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  }

  function handleSave() {
    if (!draft) return;
    update
      .save({
        track_a_delay_minutes: draft.track_a_delay_minutes,
        track_b_max_touches: draft.track_b_max_touches,
        paused: draft.paused,
      })
      .then((res) => {
        setSaved(true);
        // Reflect the clamped effective values the server returned.
        setDraft({
          track_a_delay_minutes: res.effective.track_a_delay_minutes,
          track_b_max_touches: res.effective.track_b_max_touches,
          paused: res.effective.paused,
        });
      }, () => {});
  }

  if (!draft) {
    return (
      <Card>
        <CardHead title="Request scheduling" />
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {q.isError
            ? 'We could not load your scheduling settings. Please try again shortly.'
            : 'Loading your scheduling settings...'}
        </p>
      </Card>
    );
  }

  const maxTouchesCap = limits?.track_b_max_touches.max ?? 3;
  const touchOptions = Array.from({ length: maxTouchesCap }, (_, i) => i + 1);

  return (
    <Card>
      <CardHead
        title="Request scheduling"
        right={<span className="link muted">When + how often we ask</span>}
      />
      <p className="muted" style={{ fontSize: 12.5, marginTop: -4, marginBottom: 18, lineHeight: 1.55 }}>
        Control when the first review request goes out after a job, how many
        gentle follow-ups we send, and pause everything any time. Nothing sends
        until messaging is turned on; these settings govern the cadence.
      </p>

      {/* PAUSE kill-switch */}
      <div style={{ marginBottom: 18 }}>
        <Switch
          label="Pause all review requests"
          checked={draft.paused}
          onChange={(v) => set('paused', v)}
        />
        {draft.paused && (
          <p className="muted" style={{ fontSize: 12, marginTop: 6, color: 'var(--gold, #B8860B)' }}>
            Paused: no review requests will be sent until you turn this off.
          </p>
        )}
      </div>

      <div className="formgrid">
        <label className="fld">
          <span className="lab">Send the first request</span>
          <select
            className="sel"
            value={draft.track_a_delay_minutes}
            onChange={(e) => set('track_a_delay_minutes', parseInt(e.target.value, 10))}
          >
            <option value={20}>20 minutes after the job</option>
            <option value={60}>1 hour after the job</option>
            <option value={240}>4 hours after the job</option>
            <option value={1440}>1 day after the job</option>
          </select>
        </label>
        <label className="fld">
          <span className="lab">Follow-up touches (max {maxTouchesCap})</span>
          <select
            className="sel"
            value={draft.track_b_max_touches}
            onChange={(e) => set('track_b_max_touches', parseInt(e.target.value, 10))}
          >
            {touchOptions.map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? 'touch' : 'touches'}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>
        For compliance, follow-up touches are capped at {maxTouchesCap} and spaced at
        least a few days apart. We never exceed that, no matter the setting.
      </p>

      <div className="actions" style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn--primary" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving...' : 'Save changes'}
        </button>
        {saved && !update.isPending && <span style={{ fontSize: 13, color: 'var(--green)' }}>Saved</span>}
        {update.error && !update.isPending && (
          <span style={{ fontSize: 13, color: 'var(--red)' }}>Could not save. Please try again.</span>
        )}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------------
   Team (live, GET/POST /team).
   --------------------------------------------------------------------- */
function roleLabel(role: string): string {
  switch (role) {
    case 'admin':
      return 'Admin';
    case 'approver':
      return 'Approver';
    case 'viewer':
      return 'Viewer';
    default:
      return role || 'Viewer';
  }
}

function TeamPanel() {
  const q = useTeam();
  const invite = useInviteTeamMember();
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('viewer');
  const [notice, setNotice] = useState<string | null>(null);

  const members = q.data?.team ?? [];

  async function handleInvite() {
    const e = email.trim();
    if (!e) return;
    setNotice(null);
    try {
      const res = await invite.invite({ email: e, role });
      setNotice(res.already_invited ? `${e} was already invited.` : `Invitation recorded for ${e}.`);
      setEmail('');
      setShowInvite(false);
    } catch {
      /* error surfaced below */
    }
  }

  return (
    <Card>
      <CardHead
        title="Team"
        right={
          <button className="btn btn--primary" onClick={() => setShowInvite((s) => !s)}>
            <Icon d={PlusPath} /> Invite
          </button>
        }
      />

      {showInvite && (
        <div className="formgrid" style={{ marginBottom: 16 }}>
          <label className="fld">
            <span className="lab">Email</span>
            <input className="inp" type="email" value={email} placeholder="Teammate's work email" onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="fld">
            <span className="lab">Role</span>
            <select className="sel" value={role} onChange={(e) => setRole(e.target.value as TeamRole)}>
              <option value="viewer">Viewer</option>
              <option value="approver">Approver</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <div className="fld full">
            <button className="btn btn--primary" onClick={handleInvite} disabled={invite.isPending || !email.trim()}>
              {invite.isPending ? 'Sending...' : 'Send invite'}
            </button>
            <p className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
              We record the invitation now. The email goes out once invites are turned on.
            </p>
          </div>
        </div>
      )}

      {notice && <p style={{ fontSize: 13, color: 'var(--green)', marginBottom: 12 }}>{notice}</p>}
      {invite.error && <p style={{ fontSize: 13, color: 'var(--red)', marginBottom: 12 }}>Could not send the invite. Please try again.</p>}

      {q.isLoading ? (
        <p className="muted" style={{ fontSize: 13 }}>Loading your team...</p>
      ) : members.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          No teammates yet. Invite someone to help approve replies.
        </p>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const accepted = Boolean(m.accepted_at);
              return (
                <tr key={m.id}>
                  <td>
                    <div className="who">
                      <Avatar initial={(m.email[0] || '?').toUpperCase()} color="#2A6FDB" />
                      <span>{m.email}</span>
                    </div>
                  </td>
                  <td className="muted">{roleLabel(String(m.role))}</td>
                  <td>
                    <Chip kind={accepted ? 'green' : 'gold'}>{accepted ? 'Active' : 'Invited'}</Chip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ---------------------------------------------------------------------
   Billing (LIVE 2026-06-23) - Subscribe/Upgrade via Stripe Checkout.

   - Current-plan display driven by GET /billing/plan.
   - ONE plan (no tier choice) + optional extra-locations, available to all.
   - Consent (Fix B1 de-bundle, 2026-06-30): a REQUIRED Terms of Service /
     Privacy box (blocks checkout, NOT SMS consent) plus a SEPARATE, OPTIONAL,
     unchecked SMS-authorization box (sms_authorized). Declining SMS still
     completes the subscription. Honors Hard Rule #6 (capture at checkout) while
     removing the forced-consent mechanic Twilio flagged (error 30923).
   - POST /billing/checkout -> redirect to the Stripe-hosted Checkout page.
   - Success/cancel handled via the ?billing= query the success/cancel URLs set.

   PRICING (ONE PRICE, locked 2026-08-06 -- retires the Starter/Pro ladder):
   $59/4 weeks, every feature included. Plain month-to-month, NO annual,
   NO founding/standard. NO upfront multi-cycle charge: after the 14-day free
   trial the first charge is one 4-week cycle ($59), then every 4 weeks.
   Multi-location: +$25/4 weeks per extra location, available to EVERY
   customer. 14-day free trial, card required (disclosed only at checkout);
   UI copy says only "cancel anytime".
   --------------------------------------------------------------------- */
const PLAN_INFO = {
  name: 'Rhody Reviews',
  price: '$59',
  blurb: 'Every feature included. One location, plus $25 for each additional one.',
} as const;

/* The single plan identifier the API accepts. Nothing to choose. */
const PLAN_TIER: BillingTier = 'basic';

function planChip(plan: BillingPlan | undefined) {
  // M2: label from the shared planLabel() so the Billing chip matches the
  // sidebar plan chip exactly (one source of truth).
  if (!plan || !plan.has_active_subscription) return <Chip kind="gray">No plan yet</Chip>;
  return <Chip kind="green">{planLabel(plan)}</Chip>;
}

function BillingPanel() {
  const hasToken = Boolean(useApiToken());
  const planQ = useQuery<BillingPlan, Error>({
    queryKey: ['billing-plan'],
    queryFn: ({ signal }) => api.getPlan(signal),
    enabled: hasToken,
    staleTime: 30_000,
  });
  const consentQ = useQuery<ConsentText, Error>({
    queryKey: ['billing-consent-text'],
    queryFn: ({ signal }) => api.getConsentText(signal),
    enabled: hasToken,
    staleTime: 5 * 60_000,
  });

  // ONE PLAN -> no tier state. PLAN_TIER is the constant the API accepts.
  const [locations, setLocations] = useState(0);
  // Required Terms of Service / Privacy acceptance (blocks checkout).
  const [terms, setTerms] = useState(false);
  // Optional, separate SMS-sending authorization. Unchecked by default;
  // declining still completes the subscription (Fix B1 de-bundle, 2026-06-30).
  const [smsAuthorized, setSmsAuthorized] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Show the owner's STORED SMS choice once the plan loads (2026-10-07).
  const storedSms = planQ.data?.sms_authorized;
  useEffect(() => {
    if (typeof storedSms === 'boolean') setSmsAuthorized(storedSms);
  }, [storedSms]);

  // Save the SMS choice on its own, outside checkout (same wording + version).
  // For clients who paid outside the Rhody Reviews checkout, and the wording's
  // own promise: "I can turn this on or off anytime in Settings."
  const queryClient = useQueryClient();
  const [smsMsg, setSmsMsg] = useState<string | null>(null);
  const saveSms = useMutation({
    mutationFn: () =>
      api.saveSmsAuthorization({
        sms_authorized: smsAuthorized,
        tcpa_consent_version: consentQ.data?.version ?? '',
      }),
    onSuccess: (res) => {
      setSmsMsg(res.sms_authorized ? 'Saved. Texting is on.' : 'Saved. Texting is off.');
      queryClient.invalidateQueries({ queryKey: ['billing-plan'] });
    },
    onError: () => setSmsMsg('Could not save just now. Please try again.'),
  });

  const checkout = useMutation({
    mutationFn: () =>
      api.startCheckout({
        tier: PLAN_TIER,
        // Ungated: any customer can buy additional locations (Steve 2026-08-06).
        locations,
        // tcpa_consent now carries the REQUIRED ToS/Privacy acceptance, not SMS.
        tcpa_consent: terms,
        tcpa_consent_version: consentQ.data?.version,
        // Optional SMS authorization, posted separately. False is allowed.
        sms_authorized: smsAuthorized,
      }),
    onSuccess: (res) => {
      // Hand off to the Stripe-hosted Checkout page.
      window.location.href = res.url;
    },
    onError: (e: unknown) => {
      setErr(e instanceof Error ? e.message : 'Could not start checkout. Please try again.');
    },
  });

  // U3: self-serve "Manage billing" -> Stripe billing portal (cancel / update
  // card / change plan). Graceful if Hopper's POST /billing/portal isn't live
  // yet (404 / not-found) or a network hiccup -- we show a plain fallback line
  // rather than a raw error.
  const [portalErr, setPortalErr] = useState<string | null>(null);
  const portal = useMutation({
    mutationFn: () => api.openBillingPortal(),
    onSuccess: (res) => {
      if (res?.url) window.location.href = res.url;
      else setPortalErr('The billing portal is not available yet. To cancel or update your card, email support@rhodyreviews.com.');
    },
    onError: (e: unknown) => {
      const status = (e as { status?: number })?.status;
      const code = (e as { message?: string })?.message ?? '';
      if (status === 404 || code === 'not_found' || code.includes('404')) {
        setPortalErr('Self-serve billing management is coming shortly. To cancel or update your card now, email support@rhodyreviews.com.');
      } else {
        setPortalErr('We could not open the billing portal just now. Please try again, or email support@rhodyreviews.com.');
      }
    },
  });

  // Surface the success/cancel signal the Stripe redirect URLs append.
  const billingResult = (() => {
    const p = new URLSearchParams(window.location.search).get('billing');
    return p === 'success' || p === 'cancelled' ? p : null;
  })();

  const plan = planQ.data;
  const info = PLAN_INFO;

  return (
    <Card>
      <CardHead title="Plan & billing" right={planChip(plan)} />

      {billingResult === 'success' && (
        <div className="alert info" style={{ marginBottom: 16 }}>
          <div className="t">
            {/* Truthful: ?billing=success is only the Stripe REDIRECT. Assert the
                subscription ONLY when the plan API actually confirms it; until
                the webhook lands, say we're confirming (never a false claim). */}
            {plan?.has_active_subscription ? (
              <>
                <b>You&apos;re subscribed.</b>
                <span style={{ lineHeight: 1.55 }}>
                  Thanks. Your 14-day free trial is active and your plan is set up.
                </span>
              </>
            ) : (
              <>
                <b>Finishing your checkout...</b>
                <span style={{ lineHeight: 1.55 }}>
                  Thanks. We&apos;re confirming your subscription with Stripe. This
                  can take a few seconds. Refresh if it doesn&apos;t update shortly.
                </span>
              </>
            )}
          </div>
        </div>
      )}
      {billingResult === 'cancelled' && (
        <p className="muted" style={{ fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 }}>
          Checkout was cancelled. You can pick a plan again any time below.
        </p>
      )}

      {/* Current plan summary */}
      <div className="statline" style={{ margin: 0 }}>
        <div className="s">
          <b>{plan?.plan_display_name ?? 'No plan yet'}</b>
          <span>{plan?.has_active_subscription ? `Status: ${plan.status}` : 'Choose a plan to start'}</span>
        </div>
        <div className="s">
          <b>14 days</b>
          <span>Free trial, cancel anytime</span>
        </div>
      </div>

      {/* U3: Manage billing (self-serve cancel / update card) -- only for a
          client with an active subscription. Backed by Stripe's billing portal
          via POST /billing/portal (Hopper). Degrades gracefully if not live. */}
      {plan?.has_active_subscription && (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setPortalErr(null);
              portal.mutate();
            }}
            disabled={portal.isPending}
          >
            {portal.isPending ? 'Opening...' : 'Manage billing'}
          </button>
          <p className="muted" style={{ fontSize: 12, margin: '8px 0 0', lineHeight: 1.5 }}>
            Update your card, change plan, or cancel any time. Cancelling is self-serve, no phone call.
          </p>
          {portalErr && (
            <p className="muted" style={{ fontSize: 12.5, margin: '8px 0 0', lineHeight: 1.5 }}>
              {portalErr}
            </p>
          )}
        </div>
      )}

      {/* THE plan. Was a Starter/Pro picker; one plan now, so this states the
          price rather than asking the owner to pick. */}
      <div style={{ marginTop: 18, display: 'grid', gap: 12 }}>
        <div
          style={{
            textAlign: 'left',
            border: '2px solid var(--accent, #1a73e8)',
            borderRadius: 12,
            padding: 14,
            background: 'var(--surface-2, rgba(26,115,232,0.05))',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <b style={{ fontSize: 15 }}>{PLAN_INFO.name}</b>
            <span style={{ fontSize: 14 }}>
              <b>{PLAN_INFO.price}</b>
              <small style={{ color: 'var(--muted)' }}>/4 weeks</small>
            </span>
          </div>
          <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>{PLAN_INFO.blurb}</span>
        </div>
      </div>

      {/* Multi-location add-on -- available to EVERY customer (Steve
          2026-08-06). Previously rendered only when Pro was selected. */}
      <label className="fld" style={{ marginTop: 14 }}>
        <span className="lab">Additional locations (+$25/4 weeks each)</span>
        <input
          className="inp"
          type="number"
          min={0}
          value={locations}
          onChange={(e) => setLocations(Math.max(0, parseInt(e.target.value || '0', 10)))}
          style={{ maxWidth: 120 }}
        />
        <span className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
          Your plan includes 1 location. Each extra location is +$25/4 weeks, no cap.
        </span>
      </label>

      {/* REQUIRED: Terms of Service / Privacy acceptance (NOT SMS consent).
          Checkout is blocked until this is checked. Fix B1, Solove 1a. */}
      <div
        style={{
          marginTop: 18,
          padding: 14,
          borderRadius: 10,
          background: 'var(--surface-2, rgba(0,0,0,0.03))',
          fontSize: 12,
          lineHeight: 1.55,
        }}
      >
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={terms}
            onChange={(e) => {
              setErr(null);
              setTerms(e.target.checked);
            }}
            style={{ marginTop: 3, flexShrink: 0 }}
          />
          <span>
            I have read and agree to the Rhody Reviews{' '}
            <a className="link" href={consentQ.data?.terms_url ?? 'https://rhodyreviews.com/terms'} target="_blank" rel="noreferrer">Terms of Service</a>{' '}
            and{' '}
            <a className="link" href={consentQ.data?.privacy_url ?? 'https://rhodyreviews.com/privacy'} target="_blank" rel="noreferrer">Privacy Policy</a>.
          </span>
        </label>
      </div>

      {/* OPTIONAL + SEPARATE: SMS-sending authorization. Unchecked by default;
          declining still completes the subscription. Fix B1, Solove 1b. */}
      <div
        style={{
          marginTop: 12,
          padding: 14,
          borderRadius: 10,
          background: 'var(--surface-2, rgba(0,0,0,0.03))',
          fontSize: 12,
          lineHeight: 1.55,
        }}
      >
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={smsAuthorized}
            onChange={(e) => {
              setErr(null);
              setSmsMsg(null);
              setSmsAuthorized(e.target.checked);
            }}
            style={{ marginTop: 3, flexShrink: 0 }}
          />
          <span>
            Optional. I authorize Rhody Reviews to send review-request and follow-up text
            messages to my own customers who have opted in, on behalf of my business. I
            confirm I will only add customers who have given me their consent to be texted.
            I can turn this on or off anytime in Settings. This is not required to subscribe,
            and is not a condition of any purchase.
            {consentQ.data && (
              <span style={{ display: 'block', marginTop: 6 }}>
                <a className="link" href={consentQ.data.sms_terms_url} target="_blank" rel="noreferrer">SMS Terms</a>
              </span>
            )}
          </span>
        </label>
        <div style={{ marginTop: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setSmsMsg(null);
              saveSms.mutate();
            }}
            disabled={saveSms.isPending || !consentQ.data}
          >
            {saveSms.isPending ? 'Saving...' : 'Save texting choice'}
          </button>
          {smsMsg && <span style={{ fontSize: 13 }}>{smsMsg}</span>}
        </div>
      </div>

      <div className="actions" style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button
          className="btn btn--primary"
          onClick={() => {
            setErr(null);
            if (!terms) {
              setErr('Please accept the Terms of Service and Privacy Policy to continue.');
              return;
            }
            checkout.mutate();
          }}
          disabled={checkout.isPending || !consentQ.data}
        >
          {checkout.isPending
            ? 'Starting checkout...'
            : plan?.has_active_subscription
              ? `Switch to ${info.name}`
              : `Subscribe to ${info.name}`}
        </button>
        {err && <span style={{ fontSize: 13, color: 'var(--red)' }}>{err}</span>}
      </div>

      <p className="muted" style={{ fontSize: 11.5, marginTop: 14, lineHeight: 1.5 }}>
        Billed every 4 weeks. Cancel anytime. Your plan stays active through the
        end of your current paid period, then stops. No refund for a period
        already started. No annual plan.
      </p>
    </Card>
  );
}

/* ---------------------------------------------------------------------
   Private feedback channel config (GET/PUT /feedback-gate).

   This is the config that lost its home when Private Feedback became an
   inbox screen in the reskin. It lives here now: the owner sets whether the
   parallel private channel is offered, WHERE a private note is routed
   (route_to), and the short intro line the customer sees on the private
   form (private_intro).

   HARD RULE #4 (FTC 16 CFR Part 465) -- this is NOT a score-based gate. The
   public Google review link is ALWAYS offered to every customer the same
   way; the private channel is a parallel option the customer can choose. We
   never screen, sort, or route customers by predicted rating. There is
   deliberately NO threshold / NPS / scoring control here. The panel copy
   states that plainly, and no dev code (raw endpoints/JSON) is exposed.
   --------------------------------------------------------------------- */
type FeedbackChannelDraft = {
  enabled: boolean;
  route_to: FeedbackRoute;
  private_intro: string;
};

/* Owner-facing labels for the three routing destinations. Plain English --
   no raw enum values shown (feedback_no_dev_code_in_app_ui). */
const FEEDBACK_ROUTE_LABELS: { value: FeedbackRoute; label: string; hint: string }[] = [
  {
    value: 'private_form',
    label: 'A private note in your inbox',
    hint: 'The customer fills out a short private form. It lands in your Private Feedback inbox.',
  },
  {
    value: 'text_owner',
    label: 'A text message to you',
    hint: 'The private note is sent to you as a text (uses your alert phone number).',
  },
  {
    value: 'email_support',
    label: 'An email to your support address',
    hint: 'The private note is emailed to your contact email.',
  },
];

function FeedbackChannelPanel() {
  const q = useFeedbackGate();
  const update = useUpdateFeedbackGate();
  const [draft, setDraft] = useState<FeedbackChannelDraft | null>(null);
  const [saved, setSaved] = useState(false);

  const loaded = q.data?.config;
  useEffect(() => {
    if (loaded && draft === null) {
      setDraft({
        enabled: loaded.enabled,
        route_to: loaded.route_to,
        private_intro: loaded.private_intro ?? '',
      });
    }
  }, [loaded, draft]);

  function set<K extends keyof FeedbackChannelDraft>(k: K, v: FeedbackChannelDraft[K]) {
    setSaved(false);
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  }

  function handleSave() {
    if (!draft) return;
    update
      .save({
        enabled: draft.enabled,
        route_to: draft.route_to,
        private_intro: draft.private_intro.trim(),
      })
      .then(() => setSaved(true), () => {});
  }

  if (!draft) {
    return (
      <Card>
        <CardHead title="Private feedback" />
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {q.isError
            ? 'We could not load your private-feedback settings. Please try again shortly.'
            : 'Loading your private-feedback settings...'}
        </p>
      </Card>
    );
  }

  const routeHint = FEEDBACK_ROUTE_LABELS.find((r) => r.value === draft.route_to)?.hint ?? '';

  return (
    <Card>
      <CardHead title="Private feedback" right={<span className="link muted">Parallel channel</span>} />
      <p className="muted" style={{ fontSize: 12.5, marginTop: -4, marginBottom: 16, lineHeight: 1.55 }}>
        Offer every customer a private way to reach you, alongside the public
        review link. It is never a filter. Everyone still gets the same public
        Google review link. This just gives a customer who would rather tell you
        something privately an easy way to do it.
      </p>

      <div style={{ marginBottom: 16 }}>
        <Switch
          label="Offer a private feedback option to customers"
          checked={draft.enabled}
          onChange={(v) => set('enabled', v)}
        />
      </div>

      <div className="formgrid">
        <label className="fld full">
          <span className="lab">Where private notes go</span>
          <select
            className="sel"
            value={draft.route_to}
            onChange={(e) => set('route_to', e.target.value as FeedbackRoute)}
            disabled={!draft.enabled}
          >
            {FEEDBACK_ROUTE_LABELS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          {routeHint && (
            <span className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{routeHint}</span>
          )}
        </label>

        <label className="fld full">
          <span className="lab">Intro shown on the private form</span>
          <textarea
            className="inp"
            rows={3}
            value={draft.private_intro}
            placeholder="e.g. Prefer to tell us directly? Send us a private note and we'll make it right."
            onChange={(e) => set('private_intro', e.target.value)}
            disabled={!draft.enabled}
            style={{ resize: 'vertical', minHeight: 72, lineHeight: 1.5 }}
          />
          <span className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            A short, friendly line the customer sees above the private note box.
          </span>
        </label>
      </div>

      <p className="muted" style={{ fontSize: 11.5, marginTop: 14, lineHeight: 1.5 }}>
        This does not screen or sort customers by rating, and there is no score
        threshold. The public review link is always shown to everyone.
      </p>

      <div className="actions" style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn--primary" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving...' : 'Save changes'}
        </button>
        {saved && !update.isPending && <span style={{ fontSize: 13, color: 'var(--green)' }}>Saved</span>}
        {update.error && !update.isPending && (
          <span style={{ fontSize: 13, color: 'var(--red)' }}>Could not save. Please try again.</span>
        )}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------------
   Missed-Call Text-Back ($39/4 weeks Pro-only add-on). GET/PUT /missed-call.
   When a customer calls the client's Rhody Reviews number and no one
   answers, we auto-text the caller back. Pro-gated: the toggle is locked
   for non-Pro clients. DRY-RUN until Twilio approval (server-enforced);
   the UI says so honestly.
   --------------------------------------------------------------------- */
function MctbPanel() {
  const hasToken = Boolean(useApiToken());
  const qc = useQueryClient();
  const q = useQuery<MctbResponse, Error>({
    queryKey: ['missed-call'],
    queryFn: ({ signal }) => api.getMissedCall(signal),
    enabled: hasToken,
    staleTime: 60_000,
  });
  const m = useMutation({
    mutationFn: (patch: MctbUpdate) => api.updateMissedCall(patch),
    onSuccess: (res) => {
      qc.setQueryData(['missed-call'], res);
    },
  });

  const [enabled, setEnabled] = useState(false);
  const [template, setTemplate] = useState('');
  const [forward, setForward] = useState('');
  const [saved, setSaved] = useState(false);
  const [proError, setProError] = useState(false);

  useEffect(() => {
    if (q.data) {
      setEnabled(Boolean(q.data.settings.enabled));
      setTemplate(q.data.settings.text_back_template || '');
      setForward(q.data.settings.forward_number || '');
    }
  }, [q.data]);

  const isPro = Boolean(q.data?.is_pro);
  const dryRun = q.data?.dry_run !== false; // default to dry-run if unknown

  function handleSave() {
    setSaved(false);
    setProError(false);
    const patch: MctbUpdate = {
      enabled: isPro ? enabled : false,
      text_back_template: template,
      forward_number: forward.trim() || null,
    };
    m.mutateAsync(patch).then(
      () => setSaved(true),
      (err: unknown) => {
        // The server is the source of truth on the Pro gate.
        if (err && typeof err === 'object' && 'status' in err && (err as { status?: number }).status === 403) {
          setProError(true);
          setEnabled(false);
        }
      },
    );
  }

  if (!q.data) {
    return (
      <Card>
        <CardHead title="Missed-Call Text-Back" />
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {q.isError
            ? 'We could not load your Missed-Call Text-Back settings. Please try again shortly.'
            : 'Loading...'}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHead
        title="Missed-Call Text-Back"
        right={<Chip kind={isPro ? 'gold' : 'gray'}>{isPro ? 'Pro add-on' : '$39/4 weeks Pro add-on'}</Chip>}
      />
      <p className="muted" style={{ fontSize: 12.5, marginTop: -4, marginBottom: 16, lineHeight: 1.55 }}>
        When a customer calls your Rhody Reviews number and no one picks up, we
        automatically text them back so you never lose the lead. $39/4 weeks add-on for Pro.
      </p>

      {!isPro && (
        <p
          className="muted"
          style={{ fontSize: 12.5, marginBottom: 16, lineHeight: 1.5, color: 'var(--gold, #B8860B)' }}
        >
          Missed-Call Text-Back is not available yet.
        </p>
      )}

      {/* The $25 Pro-only toggle. Disabled (locked) for non-Pro clients. */}
      <div style={{ marginBottom: 18 }}>
        <Switch
          label="Turn on Missed-Call Text-Back ($39/4 weeks)"
          checked={enabled}
          disabled={!isPro}
          onChange={(v) => {
            setProError(false);
            setEnabled(v);
          }}
        />
        {proError && (
          <p className="muted" style={{ fontSize: 12, marginTop: 6, color: 'var(--red)' }}>
            That add-on requires the Pro plan.
          </p>
        )}
      </div>

      <div className="formgrid">
        <label className="fld" style={{ gridColumn: '1 / -1' }}>
          <span className="lab">Text-back message</span>
          <textarea
            className="inp"
            rows={3}
            value={template}
            maxLength={480}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder="Sorry we missed your call! Reply here and we will get right back to you."
          />
          <span className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            {template.length}/480 characters. Sent to the caller right after a missed call.
          </span>
        </label>
        <label className="fld" style={{ gridColumn: '1 / -1' }}>
          <span className="lab">Your business phone (where calls forward first)</span>
          <input
            className="inp"
            type="tel"
            value={forward}
            onChange={(e) => setForward(e.target.value)}
            placeholder="Full number with country code"
          />
          <span className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            Use the full number with country code, e.g. +14015551234.
          </span>
        </label>
      </div>

      {/* Setup instructions: how to forward their real line to the RR number. */}
      <div
        style={{
          marginTop: 18,
          padding: 14,
          borderRadius: 10,
          background: 'var(--surface-2, rgba(0,0,0,0.03))',
          fontSize: 12.5,
          lineHeight: 1.6,
        }}
      >
        <strong>How to set it up</strong>
        <ol style={{ margin: '8px 0 0', paddingLeft: 18 }}>
          <li>
            Forward your business line to your Rhody Reviews number so calls ring
            through us first. (On most carriers: dial *72 then your Rhody Reviews
            number; *73 turns it off.)
          </li>
          <li>Enter your real business phone above as the number we ring first.</li>
          <li>
            If no one answers, we automatically text the caller your message above.
          </li>
        </ol>
        <p className="muted" style={{ margin: '8px 0 0' }}>
          Not sure of your carrier's forwarding code? Email us at
          support@rhodyreviews.com and we will walk you through it.
        </p>
      </div>

      {dryRun && (
        <p className="muted" style={{ fontSize: 12, marginTop: 14, lineHeight: 1.5 }}>
          Text-backs are not live yet. Your settings save now and turn on
          automatically once carrier approval clears. Nothing is texted in the
          meantime.
        </p>
      )}

      <div className="actions" style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn--primary" onClick={handleSave} disabled={m.isPending}>
          {m.isPending ? 'Saving...' : 'Save changes'}
        </button>
        {saved && !m.isPending && <span style={{ fontSize: 13, color: 'var(--green)' }}>Saved</span>}
        {m.error && !m.isPending && !proError && (
          <span style={{ fontSize: 13, color: 'var(--red)' }}>Could not save. Please try again.</span>
        )}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------------
   Support + Account footer (verbatim from the prototype Settings @1587-1601).

   HARD RULE: chat-only support -- NO phone number anywhere in the UI. The
   "Need a hand?" card offers a chat button only (matching the app's other
   chat-only support surfaces, e.g. Tutorials). "Account" logs out via the
   EXISTING Supabase session signOut (no new auth logic) -- same call the
   shared LogoutButton uses.
   --------------------------------------------------------------------- */
const ChatIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.7-.8L3 21l1.9-5.7a8.5 8.5 0 1 1 16.1-3.8z" />
  </svg>
);

function SupportAndAccount() {
  const { signOut } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  async function onLogout() {
    setBusy(true);
    try {
      await signOut();
    } finally {
      // M4: drop the rr_ token + wipe the query cache so the next login on a
      // shared machine cannot flash the prior tenant's data. U2: also clear
      // saved onboarding progress.
      setToken(null);
      queryClient.clear();
      clearOnboardingState();
      navigate('/auth', { replace: true });
    }
  }

  return (
    <div className="stp-footer">
      {/* Need a hand? -- async support only (no phone number, HARD RULE).
          AUDIT C4: the old "Start a chat" button was dead (no handler, no
          widget). Until the LiveChat affiliate widget is wired (needs
          Steve's LiveChat account), support is a real mailto -- an honest,
          working channel beats a fake chat promise. Copy matches what the
          button actually does. */}
      <div className="stp-foot-card">
        <div className="stp-foot-card__title">Need a hand?</div>
        <div className="stp-foot-card__desc">
          Email support and Rhody Reviews gets back to you fast. No phone tag, no tickets.
        </div>
        <a
          className="stp-chatbtn"
          style={{ textDecoration: 'none' }}
          href="mailto:support@rhodyreviews.com?subject=Rhody%20Reviews%20support"
        >
          {ChatIcon}
          Email support
        </a>
      </div>

      {/* Account -- sign out of this device. */}
      <div className="stp-foot-card">
        <div className="stp-foot-card__title">Account</div>
        <div className="stp-foot-card__desc">
          Sign out of this device or manage your team above.
        </div>
        <button type="button" className="stp-logout" onClick={onLogout} disabled={busy}>
          {busy ? 'Logging out...' : 'Log out'}
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------
   Danger Zone -- self-service ACCOUNT DELETION (2026-07-13, Hopper).

   Wired to DELETE /api/v1/account. Honors the published Data Deletion page
   (rhodyreviews.com/data-deletion) + Privacy Policy Section 6 + Hard Rule #7:
     - Clear, honest disclosure BEFORE the destructive action (what is deleted,
       what is retained for 4 years and why, that an active subscription is
       cancelled, that it is permanent).
     - A real confirmation gate: the owner must type DELETE or their exact
       business name -- a bare button never deletes.
     - On success: sign out + land on a plain confirmation (Auth ?deleted=1).

   Rhody Reviews is the ACTOR in all copy. No em dashes. National framing.
   --------------------------------------------------------------------- */
function DangerZonePanel() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut } = useSession();
  const businessQ = useBusiness();
  const planQ = usePlan();

  const businessName = (businessQ.data?.profile?.business_name ?? '').trim();
  const hasActiveSub = Boolean(planQ.data?.has_active_subscription);

  // Two-step: the destructive control stays hidden behind an explicit "arm"
  // click so an accidental tap can never reach the confirm field.
  const [armed, setArmed] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const typed = confirmText.trim();
  const matches =
    typed.toUpperCase() === 'DELETE' ||
    (businessName.length > 0 && typed.toLowerCase() === businessName.toLowerCase());

  const del = useMutation({
    mutationFn: () => api.deleteAccount(typed),
    onSuccess: async () => {
      // Teardown mirrors Log out: kill the Supabase session + rr_ token + cache
      // + saved onboarding progress, then land on the plain confirmation. The
      // account (and its auth user) is already gone server-side.
      try {
        await signOut();
      } finally {
        setToken(null);
        queryClient.clear();
        clearOnboardingState();
        navigate('/auth?deleted=1', { replace: true });
      }
    },
    onError: () => {
      setErr(
        'We could not delete your account just now. Please try again, or email privacy@rhodyreviews.com.',
      );
    },
  });

  return (
    <Card className="dz-card">
      <CardHead title="Delete account" />

      <p className="dz-lead">
        Deleting your account is permanent and cannot be undone. Rhody Reviews
        will remove your business data from its active systems right away.
      </p>

      <div className="dz-block">
        <div className="dz-block__label">What gets deleted</div>
        <ul className="dz-list">
          <li>Your business profile, brand voice, and settings.</li>
          <li>Your customers and every uploaded contact record.</li>
          <li>Your imported reviews, reply drafts, and review requests.</li>
          <li>Your connected Google listing and any connected social accounts.</li>
        </ul>
      </div>

      <div className="dz-block">
        <div className="dz-block__label">What Rhody Reviews keeps, and why</div>
        <p className="dz-note">
          To meet the Telephone Consumer Protection Act, Rhody Reviews keeps its
          audit log of message sends, consent, and opt-out events for at least
          four years. These records are never used for marketing. Backups roll
          off on their own within thirty-five days and are not restored after a
          deletion. Full detail is on the{' '}
          <a href="https://rhodyreviews.com/data-deletion" target="_blank" rel="noopener noreferrer">
            Data Deletion page
          </a>.
        </p>
      </div>

      {hasActiveSub && (
        <div className="dz-block">
          <div className="dz-block__label">Your subscription</div>
          <p className="dz-note">
            Your active plan will be cancelled as part of the deletion. You will
            not be billed again.
          </p>
        </div>
      )}

      {!armed ? (
        <button
          type="button"
          className="dz-btn dz-btn--arm"
          onClick={() => { setArmed(true); setErr(null); }}
        >
          Delete my account
        </button>
      ) : (
        <div className="dz-confirm">
          <label className="dz-confirm__label" htmlFor="dz-confirm-input">
            {businessName
              ? <>Type <b>{businessName}</b> or the word <b>DELETE</b> to confirm.</>
              : <>Type the word <b>DELETE</b> to confirm.</>}
          </label>
          <input
            id="dz-confirm-input"
            className="dz-confirm__input"
            type="text"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={confirmText}
            onChange={(e) => { setConfirmText(e.target.value); setErr(null); }}
            placeholder="DELETE"
            disabled={del.isPending}
          />
          <div className="dz-actions">
            <button
              type="button"
              className="dz-btn dz-btn--danger"
              disabled={!matches || del.isPending}
              onClick={() => { setErr(null); del.mutate(); }}
            >
              {del.isPending ? 'Deleting...' : 'Permanently delete account'}
            </button>
            <button
              type="button"
              className="dz-btn dz-btn--cancel"
              onClick={() => { setArmed(false); setConfirmText(''); setErr(null); }}
              disabled={del.isPending}
            >
              Keep my account
            </button>
          </div>
          {err && <div className="dz-error" role="alert">{err}</div>}
        </div>
      )}
    </Card>
  );
}

/* ---------------------------------------------------------------------
   Settings -- SINGLE SCROLLING COLUMN, ported 1:1 from the Claude Design
   prototype ("Rhody Reviews.dc.html" Settings @1486-1604 desktop, @2594-2631
   mobile). The prototype's Settings is one column of white rounded section
   cards under a sticky header -- NOT the old subnav-with-panels layout. We
   reshape the presentation to match while keeping EVERY existing settings
   panel + its live data/handlers wired and untouched:

     Appearance (theme swatches, self-carded via .appcard)
     Business profile   (GET/PUT /business, pull-from-Google)
     Brand voice        (GET/PUT /business brand_voice; AI drafts only)
     Booking            (brand_voice booking blob + Google Calendar connect)
     Request scheduling (GET/PUT /cadence-preferences)
     Notifications      (GET/PUT /notification-preferences)
     Missed-Call Text-Back (parked behind MCTB_UI_ENABLED -- preserved)
     Integrations       (GET /me + /connectors; Google OAuth, key connectors)
     Team               (GET/POST /team)
     Billing            (Stripe Checkout: /billing/plan, /consent-text, /checkout)
     Support + Account footer (chat-only support + log out)

   Each panel renders its own <Card>; .stp-card > .card flattens that inner
   box so only the prototype's section wrapper shows the chrome (settings.css).
   The NAV / PanelId subnav machinery above is retired; kept in the file only
   if a future tab view is wanted. This is ONE responsive component (HANDOFF
   section 8): single column both, roomier at desktop, tighter under 768px.
   --------------------------------------------------------------------- */
/* ---------------------------------------------------------------------
   Settings sections, in render order. Each gets a stable id (anchor target
   + rail key) and a short rail label. The rail + the active-section
   highlight are both driven off this ONE list, so adding/reordering a
   section needs no other change. `wrapped:false` = the panel is already a
   complete section card (AppearanceCard) and must not get the .stp-card box.
   --------------------------------------------------------------------- */
interface SettingsSectionDef {
  id: string;
  label: string;
  node: React.ReactNode;
  wrapped?: boolean; // default true -> wrap in .stp-card
}

function useSettingsSections(): SettingsSectionDef[] {
  const sections: SettingsSectionDef[] = [
    /* The Appearance section (Light / Dark / Outdoor swatches) is RETIRED
       (Steve, 2026-08-06). The app is outdoor high-contrast, always. */
    { id: 'profile', label: 'Business profile', node: <ProfilePanel /> },
    { id: 'voice', label: 'Brand voice', node: <VoicePanel /> },
    { id: 'booking', label: 'Booking', node: <BookingPanel /> },
    { id: 'scheduling', label: 'Scheduling', node: <SchedulingPanel /> },
    { id: 'notifications', label: 'Notifications', node: <NotificationsPanel /> },
    { id: 'feedback', label: 'Private feedback', node: <FeedbackChannelPanel /> },
  ];
  if (MCTB_UI_ENABLED) {
    sections.push({ id: 'mctb', label: 'Missed-call text', node: <MctbPanel /> });
  }
  sections.push(
    { id: 'integrations', label: 'Integrations', node: <IntegrationsPanel /> },
    { id: 'team', label: 'Team', node: <TeamPanel /> },
    { id: 'billing', label: 'Billing', node: <BillingPanel /> },
    { id: 'danger', label: 'Delete account', node: <DangerZonePanel /> },
  );
  return sections;
}

/* Sticky section rail (U10). Lists every section; the entry for the section
   currently in view / holding focus is highlighted in sync with the in-page
   active-section highlight. Clicking scrolls to that section. Hidden on
   narrow screens (the single column is short enough to scroll there). */
function SettingsRail({
  sections,
  activeId,
  onJump,
}: {
  sections: SettingsSectionDef[];
  activeId: string | null;
  onJump: (id: string) => void;
}) {
  return (
    <nav className="setrail" aria-label="Settings sections">
      <div className="setrail__inner">
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            className={'setrail__item' + (s.id === activeId ? ' setrail__item--active' : '')}
            aria-current={s.id === activeId ? 'true' : undefined}
            onClick={() => onJump(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

export function Settings() {
  const sections = useSettingsSections();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string | null>(sections[0]?.id ?? null);

  // Track which section is in view so the rail highlight stays in sync with the
  // in-page :focus-within highlight. IntersectionObserver picks the section
  // whose top is nearest the header; focusing a control inside a section also
  // promotes it to active (so tabbing through updates the rail too).
  useEffect(() => {
    const els = sections
      .map((s) => document.getElementById('set-' + s.id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        // Choose the topmost section currently intersecting the viewport.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.id.replace(/^set-/, '');
          setActiveId(id);
        }
      },
      // Bias the "active" band to just under the sticky header.
      { rootMargin: '-88px 0px -55% 0px', threshold: [0, 0.1, 0.5] },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [sections]);

  // Deep-link support: /settings#billing (or any #<section-id>) scrolls to and
  // activates that section on load. Used by the TrialBar "Upgrade" CTA (U16).
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    const el = document.getElementById('set-' + hash);
    if (el) {
      // Let the panels mount first, then scroll.
      const t = window.setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveId(hash);
      }, 60);
      return () => window.clearTimeout(t);
    }
  }, []);

  // ACTIVE-FIELD HIGHLIGHT (Steve's redo): the focused field's .fld wrapper
  // lights up blue/white; when focus moves, the previous field reverts and the
  // new one lights up -- one at a time, travelling down the form. Delegated
  // focusin/focusout on the body toggles a .fld--active class (a JS fallback
  // that guarantees the effect even where CSS :focus-within is finicky). We
  // also promote the containing section to the rail-active state.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    let current: HTMLElement | null = null;
    const clear = () => {
      if (current) {
        current.classList.remove('fld--active');
        current = null;
      }
    };
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Light up the closest labelled field wrapper.
      const fld = target.closest('.fld') as HTMLElement | null;
      if (fld !== current) {
        clear();
        if (fld) {
          fld.classList.add('fld--active');
          current = fld;
        }
      }
      // Keep the section rail in sync with where focus is.
      const section = target.closest('.setsection') as HTMLElement | null;
      if (section?.id) setActiveId(section.id.replace(/^set-/, ''));
    };
    const onFocusOut = (e: FocusEvent) => {
      // Clear only when focus leaves the current field entirely.
      const next = e.relatedTarget as Node | null;
      if (current && (!next || !current.contains(next))) clear();
    };
    body.addEventListener('focusin', onFocusIn);
    body.addEventListener('focusout', onFocusOut);
    return () => {
      body.removeEventListener('focusin', onFocusIn);
      body.removeEventListener('focusout', onFocusOut);
      clear();
    };
  }, []);

  const jump = useCallback((id: string) => {
    const el = document.getElementById('set-' + id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  }, []);

  return (
    <div className="setpage">
      {/* Sticky top header (position:sticky in settings.css) now carries an
          always-visible "Back to dashboard" exit. Previously the ONLY way back
          out of this long page was an element anchored at the very BOTTOM, so an
          owner mid-scroll felt trapped (Steve, 2026-08-05). Keeping the exit in
          the sticky header means it floats down the page and is always reachable
          without scrolling -- on mobile especially, where the sidebar + section
          rail are both hidden. */}
      <div
        className="setpage__head"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      >
        <div>
          <h1 className="setpage__title">Settings</h1>
          <p className="setpage__sub">Your business profile, notifications, and plan.</p>
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => navigate('/dashboard')}
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to dashboard
        </button>
      </div>

      <div className="setpage__layout">
        <SettingsRail sections={sections} activeId={activeId} onJump={jump} />

        <div className="setpage__body" ref={bodyRef}>
          {sections.map((s) => (
            <section
              key={s.id}
              id={'set-' + s.id}
              data-section={s.id}
              className={'setsection' + (s.wrapped === false ? ' setsection--bare' : '')}
            >
              {s.wrapped === false ? s.node : <div className="stp-card">{s.node}</div>}
            </section>
          ))}

          <SupportAndAccount />
        </div>
      </div>
    </div>
  );
}
